import DOMPurify from "dompurify";
import { useCartContext } from "../Contexts/CartContext";
import { PRODUCT_TYPE_COMPOSITE, VIEW_MODE_GRID, VIEW_MODE_IMAGE_GALLERY, VIEW_MODE_LIST } from "./Constants/Product";
import { ORDER_TYPE_DELIVERY, ORDER_TYPE_TAKEAWAY, ORDER_TYPE_DINE_IN, ORDER_TYPE_SCHEDULING, ORDER_TYPE_STORE_COUNTER } from "./Constants/Service";
import { formatZipcode } from "./Formatters";
import { useOrderTypeContext } from "../Contexts/OrderTypeContext";
import { ORDER_STATUS_CANCELED, ORDER_STATUS_DONE, ORDER_STATUS_EXPIRED } from "./Constants/Checkout";

//* Vibração para 'confirmar' ações de cliques.
export const vibrate = (pattern: number = 30) => {
    if (navigator.vibrate){
        navigator.vibrate(pattern)
    }
}

export const isPhoneValid = (phone: string) => {
    return Boolean( phone?.length && phone?.length === 11)
}

export const isCpfValid = (cpf: string): boolean => {
    if (!cpf) return false;

    // Remove tudo que não for número
    cpf = cpf.replace(/[^\d]+/g, '');

    // CPF deve ter exatamente 11 dígitos
    if (cpf.length !== 11) return false;

    // Elimina CPFs inválidos conhecidos (ex: 111.111.111-11)
    if (/^(\d)\1{10}$/.test(cpf)) return false;

    let sum = 0;
    let remainder;

    // Valida primeiro dígito verificador
    for (let i = 1; i <= 9; i++) {
        sum += parseInt(cpf.substring(i - 1, i)) * (11 - i);
    }
    remainder = (sum * 10) % 11;
    if (remainder === 10 || remainder === 11) remainder = 0;
    if (remainder !== parseInt(cpf.substring(9, 10))) return false;

    // Valida segundo dígito verificador
    sum = 0;
    for (let i = 1; i <= 10; i++) {
        sum += parseInt(cpf.substring(i - 1, i)) * (12 - i);
    }
    remainder = (sum * 10) % 11;
    if (remainder === 10 || remainder === 11) remainder = 0;
    if (remainder !== parseInt(cpf.substring(10, 11))) return false;

    return true;
};

export const useQuantityProductInCartConference = (productId: number): number => {
    const { cartConference, currentCart } = useCartContext();
    const { isStoreCounter } = useOrderTypeContext();

    const cartTarget = isStoreCounter ? cartConference : currentCart?.items;
    if (!cartTarget || (cartTarget && cartTarget.length === 0)) return 0;

    const cartFilteredByOrderType =
        isStoreCounter
            ?   cartTarget.filter((product: any) => (product.id === productId || (product.idParentProduct && product.idParentProduct === productId)))
            :   cartTarget.filter((product: any) => (product.product_id === productId || (product.idParentProduct && product.idParentProduct === productId)))

    return cartFilteredByOrderType
        .reduce((total: number, product: any) => {
            //* Soma a quantity do próprio produto, caso seja SIMPLE
            let productTotal = product.type === PRODUCT_TYPE_COMPOSITE ? 0 : product.quantity;

            //* Se o produto tiver steps, somar a quantity de todos os complements
            if (product.steps) {
            const stepQuantity = product.steps.reduce((stepTotal: number, step: any) => {
                return stepTotal + (step.complements?.reduce((complementTotal: number, complement: any) => {
                return complementTotal + complement.quantity;
                }, 0) || 0);
            }, 0);

            //* Soma o total de complements ao total do produto
            productTotal += stepQuantity;
            }

        return total + productTotal;
    }, 0);
}

export const isArraysEqual = (arr1: any, arr2: any): boolean => {
    if (arr1.length !== arr2.length) return false;
    for (let i = 0; i < arr1.length; i++) {
        if (arr1[i] !== arr2[i]) return false;
    }
    return true;
}

export const normalizeStringToSearch = (value: string): string => {
    return value.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
}

export const copyToClipboard = async (value: string) => {
    try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
            await navigator.clipboard.writeText(value);
        } else {
            const textArea = document.createElement("textarea");
            textArea.value = value;
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand("copy");
            document.body.removeChild(textArea);
        }
        return true;
    } catch (err) {
        console.error("Failed to copy: ", err);
        return false;
    }
}

export const hideKeyboard = async () => {
    if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
    }
}

export interface StoreWorkdays {
    id: number;
    store_id: number;
    service_type: number;
    weekday: number;
    operation_from: string;
    operation_to: string;
    active: boolean;
}

export function generateOperatingHoursText(storeWorkdays: StoreWorkdays[], onlyOpeningHours:boolean=false): string {
    const weekdays: string[] = ["Domingo", "Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado"];
    let hoursText: string = "";
    storeWorkdays.forEach(service => {
        const day = weekdays[service.weekday];
        const from = service.operation_from ? service.operation_from.slice(0, 5).replace(/:00$/, '') : 'Fechado';
        const to = service.operation_to ? service.operation_to.slice(0, 5).replace(/:00$/, '') : '';
        if (from === 'Fechado') {
            hoursText += `<strong>${day}</strong>: Fechado<br />`;
        } else if (onlyOpeningHours) {
            hoursText = `${from}h`;
        } else {
            hoursText += `<strong>${day}</strong>: ${from}h às ${to}h<br />`;
        }
    });
    return hoursText;
}

export const getFirstAndLastDayOfMonth = (year?: number, month?: number) => {
    const currentDate = new Date();
    const currentYear = year || currentDate.getFullYear();
    const currentMonth = month || (currentDate.getMonth() + 1); // getMonth() retorna de 0 a 11
    const firstDay = new Date(currentYear, currentMonth - 1, 1);
    const lastDay = new Date(currentYear, currentMonth, 0);

    // 'YYYY-MM-DD'
    const formatDate = (date: Date) => date.toISOString().split('T')[0];

    return {
        date_from: formatDate(firstDay),
        date_to: formatDate(lastDay),
    };
}

export const getPercentageDiscount = (price: number, priceFrom: number): number => {
    if (priceFrom === 0) {
        throw new Error("priceFrom não pode ser zero.");
    }
    const percent = ((price - priceFrom) / priceFrom) * 100;

    return Math.round(percent * 100) / 100 ;
}

export const compareEquals = (obj1: any, obj2: any) => {
    // Verifica se são o mesmo tipo de dado
    if (typeof obj1 !== 'object' || typeof obj2 !== 'object' || obj1 === null || obj2 === null) {
        return obj1 === obj2;
    }

    // Verifica se possuem o mesmo número de chaves
    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);

    if (keys1.length !== keys2.length) {
        return false;
    }

    // Verifica se cada chave tem o mesmo valor
    for (let key of keys1) {
        if (!compareEquals(obj1[key], obj2[key])) {
            return false;
        }
    }

    return true;
}

export const convertDateToTimestamp = (dateString: string) => {
    //*dateString no formato '23/10/2024 17:43 UTC -03:00'
    const formattedDateString = dateString.replace(/(\d{2})\/(\d{2})\/(\d{4})/, '$3-$2-$1');
    const date = new Date(formattedDateString);
    return date.getTime();
}

export const getWeekdayInPortuguese = (weekday: number, abbreviated: boolean = false): string => {
    const weekdays = abbreviated
                        ? ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb']
                        : ['domingo', 'segunda', 'terça', 'quarta', 'quinta', 'sexta', 'sábado'];
    return weekdays[weekday];
}

export function isPastHours(timestamp: number, hours: number = 2): boolean {
    if (!Number.isFinite(timestamp) || timestamp <= 0) {
      console.warn('Timestamp inválido:', timestamp);
      return false;
    }
    const currentTime = Date.now();
    const difference = currentTime - timestamp;
    return difference >= hours * 60 * 60 * 1000;
}

export function isNewProductOnCatalog(tagsGroup: any): boolean {
    return tagsGroup?.some((tag: any) => tag.slug === "newproductoncatalog");
    // return tagsGroup?.some(
    //     (group: any) =>
    //         group.slug === "highlights" &&
    //         group.tags?.some((tag: any) => tag.slug === "novo")
    //     );
}

export function hasProductFlagTag(tagsGroup: any): boolean {
    return false; //VERIFCAR REGRA DE TAGS PARA EXIBIR FLAG
    // return tagsGroup?.some(
    //     (group: any) =>
    //         group.slug === "highlights" &&
    //         group.tags?.some((tag: any) => tag.slug === "novo")
    //     );
}

export function storeIsOpenByOrderType(orderType: string, store: any): boolean {
    if (store.closed) { 
        return false;
    }

    let cleanedOrderType = orderType?.replace(/_/g, '');

    //* Aguardando backend retornar o "storecounter_open"
    if (cleanedOrderType === 'storecounter') {
        cleanedOrderType = 'balcao';
    }

    const key = `${cleanedOrderType}_open`;
    return store[key] === true;
}

export function orderTypeAsSlug(orderType: string): string {
    if (!orderType) return '';
    const slug = orderType.replace(/[_\-\s]/g, '').toLowerCase();
    switch (slug) {
        case 'storecounter': return 'comanda'
        case 'dinein': return 'papa-fila'
        case 'papafila': return 'papa-fila'
        //* utilizados para qrcodes antigos
        case 'autoatendimento': return 'comanda'
        default: return slug;
    }
}

export function orderTypeAsSlugConvert(orderType: string): string {
    const orderTypeSlug = orderType.replace(/[_\-\s]/g, '').toLowerCase();
    switch (orderTypeSlug) {
        case 'delivery': return ORDER_TYPE_DELIVERY
        case 'takeaway': return ORDER_TYPE_TAKEAWAY
        case 'papafila': return ORDER_TYPE_DINE_IN
        case 'scheduling':
        case 'encomenda': return ORDER_TYPE_SCHEDULING
        case 'storecounter':
        case 'comanda': return ORDER_TYPE_STORE_COUNTER
        //* utilizados para qrcodes antigos
        case 'dinein': return ORDER_TYPE_DINE_IN
        case 'autoatendimento': return ORDER_TYPE_STORE_COUNTER
        default: return '';
    }
}

export function sanitizeText(text: string) {
    return DOMPurify.sanitize(text);
}

export function formatAddressStore(store: any) {
    var address = store.address
    address += store.number && ', ' + store.number
    address += store.neighborhood && ', ' + store.neighborhood
    address += store.city && ', ' + store.city
    address += store.state && ' - ' + store.state
    address += store.zip_code && ' - CEP: ' + formatZipcode(store.zip_code)
    return address;
}

export function goScrollTo(target?: HTMLElement, add: number = 0) {
    const box = document.getElementById('boxPaymentMethods');
    const boxHeight = box?.offsetHeight ?? 0;

    setTimeout(() => {
      if (!target) return;
      const rect = target.getBoundingClientRect();
      const top = rect.top + window.scrollY - boxHeight - add - 30; //30 de margem
      window.scrollTo({ top, behavior: 'smooth' });
    }, 50);
}

export function goScrollToTop() {
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 50);
  }

export function findTableDataById(tableCategories: any, tableId: number) {
    for (const category of tableCategories) {
        const table = category.tables.find((table: any) => table.id === tableId);
        if (table) {
            return table;
        }
    }
    return null;
}

export function isIOS() {
    return /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
}

export function delayTime(ms: number) {
    return new Promise(resolve => setTimeout(resolve, ms));
}

export function calculateDiscountPercentage(fromPrice: number, price: number): number {
    if (!fromPrice || fromPrice <= 0) return 0;
    return ((fromPrice - price) / fromPrice) * 100;
}

export function isOrderOpen(order: any) {
    const status = Number(order.status);
    return (
        status !== ORDER_STATUS_DONE &&
        status !== ORDER_STATUS_EXPIRED &&
        status !== ORDER_STATUS_CANCELED
    );
}

export const updateUrlWithProductSlug = (url: string) => {
    if (!url) return;
    window.history.replaceState({}, "", url);
};
export function isValidViewMode(getViewMode: string): boolean {
    return getViewMode === VIEW_MODE_GRID || getViewMode === VIEW_MODE_LIST || getViewMode === VIEW_MODE_IMAGE_GALLERY
}

export function cropString(str: string, maxLength: number = 20): string {
    if (!str) return "";
    return str.length > maxLength ? str.slice(0, maxLength) + "..." : str;
}

export function isAllergen(product: any): boolean {
    return false;
    // return product.id === 31730 || product.id === 31754;
}

export function getCategoryProductCount(category: any): number {
  let total = category.products?.length || 0;

  if (category.children?.length) {
    total += category.children.reduce((sum: number, child: any) => {
      return sum + getCategoryProductCount(child);
    }, 0);
  }

  return total;
};

// Usado para desabilitar o botão de filtro caso não tenha produtos com a tag
export interface TagFilter {
    id: number;
    name: string;
    slug: string;
    group_slug?: string;
    icon?: string | null;
}

const productMatchesFilters = (product: any, selectedTags: TagFilter[]): boolean => {
    if (!selectedTags || selectedTags.length === 0) return true;

    const productTagIds = product.tags?.map((tag: any) => tag.id) || [];

    // Separamos as tags por grupo/comportamento
    const wineBlockedTags = selectedTags.filter(tag => (tag.group_slug === "wine-country" || tag.group_slug === "wine-type"));
    const otherTags = selectedTags.filter(tag => tag.group_slug !== "wine-country" && tag.group_slug !== "wine-type");

    // Se houver seleção de país ou tipo ('wine-country' ou 'wine-type'), o produto DEVE ter o país e o tipo selecionado.
    const matchesWineCountry = wineBlockedTags.length === 0 ||
        wineBlockedTags.every(tag => productTagIds.includes(tag.id));

    // Se houver outras seleções, o produto deve conter pelo menos uma delas.
    const matchesOthers = otherTags.length === 0 ||
        otherTags.some(tag => productTagIds.includes(tag.id));

    return matchesWineCountry && matchesOthers;
};

export function hasProductWithTag(category: any, tag: TagFilter, selectedTags: TagFilter[] = []): boolean {

    const checkProduct = (product: any) => {
        const productTagIds = product.tags?.map((t: any) => t.id) || [];

        // 1. O produto deve ter obrigatoriamente a tag que estamos testando
        if (!productTagIds.includes(tag.id)) return false;

        // 2. O produto deve respeitar os filtros MANDATÓRIOS (AND) já selecionados (país e tipo)
        const wineBlockedTags = selectedTags.filter(t => (t.group_slug === "wine-country" || t.group_slug === "wine-type"));

        if (wineBlockedTags.length > 0) {
            // Se algum filtro mandatório for selecionado, o produto deve conter TODOS eles
            if (!wineBlockedTags.every(t => productTagIds.includes(t.id))) return false;
        }

        return true;
    };

    // Verifica produtos diretos
    const hasInDirect = category.products?.some(checkProduct);

    if (hasInDirect) return true;

    // Verifica nas subcategorias
    return category.children?.some((child: any) =>
        hasProductWithTag(child, tag, selectedTags)
    ) || false;
}

export function filterCategoryByTags(category: any, selectedTags: TagFilter[]): any {
    if (!selectedTags || !selectedTags.length) return category;

    const filteredProducts = category.products?.filter((product: any) =>
        productMatchesFilters(product, selectedTags)
    ) || [];

    const filteredChildren = category.children
        ?.map((child: any) => filterCategoryByTags(child, selectedTags))
        .filter((child: any) =>
            (child.products?.length > 0) ||
            (child.children?.length > 0)
        ) || [];

    return {
        ...category,
        products: filteredProducts,
        children: filteredChildren,
    };
}